import { positionTypes, rowTypes, } from "../types/DocumentTypes";
import { sbvPermissions } from "../types/LicencesTypes";
import { getVatAsPrintString } from "../types/PrintTypes";
import Big from "big.js";
import { _ } from "svelte-i18n";
import { get } from "svelte/store";
import { formatBigWithLocale, moneyRound } from "./Parser";
export function calculateDocument(document, amountPrecision) {
    //Create the condition packages and calculate the new grad total for the document.
    return createInvoiceConditionPackage(document, amountPrecision, document.rapports);
}
export function getPostTotalForPositionList(positions, documentCalculationFactorGroup, amountPrecision) {
    let total = new Big(0);
    positions.forEach((position) => {
        total = total.plus(getPostTotal(position, documentCalculationFactorGroup, amountPrecision));
    });
    return total;
}
export function getReducedTotalForPositionList(positions, documentCalculationFactorGroup, amountPrecision) {
    let total = new Big(0);
    positions.forEach((position) => {
        total = total.plus(getReducedTotal(position, documentCalculationFactorGroup, amountPrecision));
    });
    return total;
}
/**
 * Get the total price of a position. This will take the final price (with reduction) and multiplies it with the amount the position has.
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @param amountPrecision
 * @returns A already formated version of the position
 */
export function getPositionTotalAsString(documentPosition, documentCalculationFactorGroup, amountPrecision) {
    return formatBigWithLocale(getReducedTotal(documentPosition, documentCalculationFactorGroup, amountPrecision));
}
/**
 * Get the price for this position with the normal prices.
 * This will take the costelements if exists or the price on the position.
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @param amountPrecision
 * @returns
 */
export function getReducedTotal(documentPosition, documentCalculationFactorGroup, amountPrecision) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    //Get the price without reduction
    let price = getPositionTotalWithoutReduction(documentPosition, documentCalculationFactorGroup, amountPrecision);
    // first calculate the factor then the reduction
    // (there will/should always be only one of them set)
    if (documentPosition.factor != null) {
        price = price.times(documentPosition.factor);
    }
    if (documentPosition.discount != null) {
        price = price.minus(price.times(documentPosition.discount).div(100));
    }
    return price.round(2);
}
/**
 * Get the price for this position with the normal prices.
 * This will take the costelements if exists or the price on the position.
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @param amountPrecision
 * @returns
 */
export function getReducedTotalForExtentAmount(documentPosition, documentCalculationFactorGroup) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    //Get the price without reduction
    let price = getPositionTotalWithoutReductionExtent(documentPosition, documentCalculationFactorGroup);
    // first calculate the factor then the reduction
    // (there will/should always be only one of them set)
    if (documentPosition.factor != null) {
        price = price.times(documentPosition.factor);
    }
    if (documentPosition.discount != null) {
        price = price.minus(price.times(documentPosition.discount).div(100));
    }
    return price.round(2);
}
/**
 * Get the total price of a position. This will take the final price (with reduction) and multiplies it with the amount the position has.
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @param amountPrecision
 * @returns
 */
export function getPositionTotalWithoutReduction(documentPosition, documentCalculationFactorGroup, amountPrecision) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    return getPriceWithoutReduction(documentPosition, documentCalculationFactorGroup).times(documentPosition.amount.round(amountPrecision)).round(2);
}
/**
 * Get the total price of a position depending on the extent information. This will take the final price (with reduction) and multiplies it with the extent the position has.
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @returns
 */
export function getPositionTotalWithoutReductionExtent(documentPosition, documentCalculationFactorGroup) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    const extentAmount = getPositionExtentTotal(documentPosition);
    return getPriceWithoutReduction(documentPosition, documentCalculationFactorGroup).times(extentAmount).round(2);
}
/**
 * Get the price of a position without any reduction.
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @returns The price without reduction
 */
export function getPriceWithoutReduction(documentPosition, documentCalculationFactorGroup) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    let price = new Big(0);
    //Get the price from the cost elements or the price on the position.
    if (documentPosition.costElements.length > 0) {
        documentPosition.costElements.forEach(item => {
            price = price.plus(getPositionCostElementPricePre(item, documentCalculationFactorGroup));
        });
    }
    else {
        price = documentPosition.price;
    }
    return price.round(2);
}
/**
 * Calculate the single price of an cost element base on the pre factors
 * @param item
 * @param documentCalculationFactorGroup
 * @returns
 */
export function getPositionCostElementPricePre(item, documentCalculationFactorGroup) {
    return item.amount.times(item.pricePerUnit)
        .round(2)
        .times(documentCalculationFactorGroup["calculationFactor" + item.documentCalculationFactorIndex])
        .round(2);
}
/**
 * Calculate the single price of an cost element base on the post factors
 * @param item
 * @param documentCalculationFactorGroup
 * @returns
 */
export function getPositionCostElementPricePost(item, documentCalculationFactorGroup) {
    return item.postAmount.times(item.postPricePerUnit)
        .round(2)
        .times(documentCalculationFactorGroup["postCalculationFactor" + item.documentCalculationFactorIndex])
        .round(2);
}
/**
 * Get the post price of a position without any reduciton
 * @param documentPosition
 * @param documentCalculationFactorGroup
 * @returns
 */
export function getPostPriceWithoutReduction(documentPosition, documentCalculationFactorGroup) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    let price = new Big(0);
    //Get the price from the cost elements or the price on the position.
    if (documentPosition.costElements.length > 0) {
        documentPosition.costElements.forEach(item => {
            price = price.plus(getPositionCostElementPricePost(item, documentCalculationFactorGroup));
        });
    }
    else {
        price = documentPosition.price;
    }
    return price.round(2);
}
/**
 * Get the total price (price*amount) for the given position with the post calc factors.
 * @param documentPosition
 * @returns
 */
export function getPostTotal(documentPosition, documentCalculationFactorGroup, amountPrecision) {
    if (documentPosition.fkSystemRowType != rowTypes.pricePosition)
        return new Big(0);
    let price = getPostPriceWithoutReduction(documentPosition, documentCalculationFactorGroup);
    // multiply by amount
    price = price.times(documentPosition.amount.round(amountPrecision)).round(2);
    //calculate the factor and the discount into the price
    if (documentPosition.factor) {
        price = price.times(documentPosition.factor);
    }
    if (documentPosition.discount) {
        price = price.minus((price.times(documentPosition.discount).div(100)));
    }
    return price.round(2);
}
/**
 * Calcualte the subtotal value of the given startingIndex and the target level.
 * @param positions
 * @param factorGroup
 * @param startingIndex
 * @param levelTarget
 * @param amountPrecision
 * @returns
 */
export function calculateSubtotal(positions, factorGroup, startingIndex, levelTarget, amountPrecision) {
    //Nah we do not do anything.
    if (startingIndex > positions.length)
        return new Big(0);
    let subtotal = new Big(0);
    //We have to loop backwards. We do not need the starting item to highlight
    for (let i = startingIndex - 1; i >= 0; i--) {
        const position = positions[i];
        if (position.level != null && position.level <= levelTarget)
            break;
        //Otherwise sumup the stuff.
        if (position.fkSystemRowType == 1 && !position.isOptional) {
            //Now minus a possible reduction
            subtotal = subtotal.plus(getReducedTotal(position, factorGroup, amountPrecision));
        }
    }
    return subtotal;
}
/**
 * Get the extent total for the given position.
 * @param documentPosition
 * @returns
 */
export function getPositionExtentTotal(documentPosition) {
    let total = new Big(0);
    documentPosition.extents.forEach(item => {
        total = total.plus(item.total);
    });
    return total;
}
/**
 * Get the price for a position catalogue position variant
 * @param variant
 * @param setting
 * @returns the price of the variant
 */
export function getCataloguePositionTotal(variant, calculationFactorGroup) {
    let total = new Big(0);
    if (variant.costElements.length > 0) {
        //Sum up the total from the cost elements
        variant.costElements.forEach(element => {
            //Get the right amount and price
            total = total.plus(getCatalogPositionVariantCostElementTotal(element, calculationFactorGroup));
        });
    }
    else {
        //Set the total to the price.
        total = new Big(variant.price || 0);
    }
    return total.round(2);
}
/**
 * Get the right total for a PositionCataloguePositionVariantCostElement.
 * This will prioritice the price of a referenced cost element over it's own.
 * @param item
 * @param calculationFactorGroup
 * @returns
 */
export function getCatalogPositionVariantCostElementTotal(item, calculationFactorGroup) {
    const refItem = item.costElement ? item.costElement : item;
    return new Big(item.amount || 0)
        .times(refItem.price || 0)
        .round(2)
        .times(calculationFactorGroup["calculationFactor" + refItem.calculationFactorGroupIndex])
        .round(2);
}
/**
 * Calculate the total money value for the extent. This will use the extent amount instead of the position amount.
 * @param document
 * @returns
 */
export function calculateDocumentExtentTotal(document) {
    let total = new Big(0);
    document.rapports.forEach(rapport => {
        rapport.positions.forEach(position => {
            //We need to sum up the price with the extent amount
            let subtotal = new Big(getPriceWithoutReduction(position, document.calculationFactors))
                .times(getPositionExtentTotal(position))
                .round(2);
            // first calculate the factor then the reduction
            // (there will/should always be only one of them set)
            if (position.factor != null) {
                subtotal = subtotal.times(position.factor);
            }
            if (position.discount != null) {
                subtotal = subtotal.minus(subtotal.times(position.discount).div(100));
            }
            total = total.plus(subtotal.round(2));
        });
    });
    return total;
}
/**
 * Calculate the amount we have to pay for the given rapports.
 * @param document The origin document
 * @param amountPrecision How many amount precision is used.
 * @param rapports The target rapports. This can be a subset or otherwise adjustest array.
 * @returns The amount to pay.
 */
export function calculateRapportsAmountToPay(document, amountPrecision, rapports) {
    //Create the condition packages and calculate the new grad total for the document.
    let calculations = createInvoiceConditionPackage(document, amountPrecision, rapports);
    //Return the amount to pay
    return calculations.amountToPay;
}
/**
 * Generat the printable condition packages from the document and the given price positions.
 * This function must adjust the grand total.
 * @param document
 * @param amountPrecision
 * @param targetData The rapport data we used for fetching the price positions
 * @returns An Object holding the adjusted grand total and the condition packages.
 */
export function createInvoiceConditionPackage(document, amountPrecision, targetData) {
    //Get all relevant positions for the calculation.
    let pricePositions = [];
    //A map for all the totals per fkSystemType
    let groupTotals = new Map();
    //A map for all rapport totals.
    let totalPerRapport = new Map();
    let startTotal = new Big(0);
    let hoursWorked = new Big(0);
    let documentTotal = new Big(0);
    //They must be initalitzed with 0 values other wise we could endup with null
    let vat0Total = new Big(0);
    let vat1Total = new Big(0);
    let vat2Total = new Big(0);
    //Check if we have a sbv position
    let hasSBVPositions = document.rapports.some(rapport => rapport.positions.some(pos => pos.permissions?.includes(sbvPermissions.sbv)));
    targetData.forEach(rapport => {
        //Keep track of the rapport total
        let rapportTotal = new Big(0);
        rapport.positions.forEach(position => {
            if (position.fkSystemRowType != 1 || position.isOptional)
                return;
            let originalPrice = getReducedTotal(position, document.calculationFactors, amountPrecision);
            const totalForGroup = groupTotals.get(position.fkSystemPositionType) || new Big(0);
            // get the document condition of level 0 with fkSystemPositionType == position.fkSystemPositionType
            let ignoreNet = document.conditions.filter(condi => condi.conditionLevel == 0 &&
                condi.fkSystemPositionType == position.fkSystemPositionType)?.[0]?.isIgnoreNet;
            //Add the total to the group
            if (!ignoreNet || !position.isNetto) {
                groupTotals.set(position.fkSystemPositionType, totalForGroup.plus(originalPrice).round(2));
            }
            //Calcualte the working hours
            if (position.unit?.toLowerCase() === "h" && position.fkSystemPositionType == positionTypes.salary) {
                hoursWorked = hoursWorked.plus(position.amount);
            }
            //Create a price position
            pricePositions.push({
                isNetto: position.isNetto,
                originalPrice: originalPrice,
                reducedPrice: originalPrice,
                fkSystemPositionType: position.fkSystemPositionType,
                documentVatSet: position.documentVatSet,
                historyPrises: [originalPrice]
            });
            startTotal = startTotal.plus(originalPrice);
            rapportTotal = rapportTotal.plus(originalPrice);
        });
        //Add the total to the map
        totalPerRapport.set(rapport.nr, rapportTotal);
    });
    let grandTotal = startTotal;
    let conditionPackages = new Map();
    //Now transform the conditions
    for (let levelIndex = 0; levelIndex < 6; levelIndex++) {
        //Get the package.
        let condiPackage = conditionPackages.get(levelIndex);
        //We have to create a new one
        if (!condiPackage) {
            condiPackage = {
                level: levelIndex,
                sum: grandTotal,
                values: []
            };
            //Add it to the map
            conditionPackages.set(levelIndex, condiPackage);
        }
        //Only go through the target conditions
        const targetConditions = document.conditions.filter(condi => condi.conditionLevel == levelIndex);
        //The Vat is a special construct we have to handle.
        //From condition level 4 (VAT) on we have to keep more precition because of horiziontal vs vertical VAT calculation!
        if (levelIndex == 4) {
            //We need a memory for the totals per vat, we use the normal index
            let vatSummary = new Map();
            //Now calculate the totals per vat. For that we have to add the vat amount on the position first.
            pricePositions.forEach(pos => {
                //Get the last total
                let summary = vatSummary.get(pos.documentVatSet) || {
                    appliedTotal: new Big(0),
                    vatTotal: new Big(0)
                };
                //Add the current price as total
                summary.appliedTotal = summary.appliedTotal.plus(pos.reducedPrice);
                //Get the vat for this position, so we know how much the actual vat is.
                let vat = document["vat" + pos.documentVatSet];
                //We need to get the right amount of vat percentage
                if (!document.isInclusiveVat) {
                    let posVat = new Big(pos.reducedPrice).div(100).times(vat);
                    pos.reducedPrice = pos.reducedPrice.plus(posVat);
                    summary.vatTotal = summary.vatTotal.plus(posVat);
                }
                else {
                    let posVat = new Big(pos.reducedPrice).div(new Big(100).plus(vat)).times(vat);
                    //Reduce the applied price so that the applied prices shows the right amount
                    summary.appliedTotal = summary.appliedTotal.minus(posVat);
                    summary.vatTotal = summary.vatTotal.plus(posVat);
                }
                //Set it again
                vatSummary.set(pos.documentVatSet, summary);
                //Save the new actual value on the condition level.
                pos.historyPrises[levelIndex] = pos.reducedPrice;
            });
            //Sort the vat's
            vatSummary = new Map([...vatSummary.entries()].sort());
            //Now we have to go through all vat sets and add a line to the condi package.
            vatSummary.forEach((summary, key) => {
                //Get the vat from the document
                let vat = document["vat" + key];
                //Add the line to the condi package.
                condiPackage.values.push({
                    priceReductionIsApplied: summary.appliedTotal,
                    reductionValue: summary.vatTotal,
                    skipped: false,
                    documentCondition: {
                        id: key,
                        name: vatSummary.size > 1 ? get(_)("documentview.lbl_vat_in_percent_multi").replace("{}", getVatAsPrintString(key) + ")") : get(_)("documentview.lbl_vat_in_percent"),
                        conditionLevel: levelIndex,
                        isSequential: true,
                        isIgnoreNet: false,
                        isFromPartner: false,
                        fkDocument: document.id,
                        creationDate: new Date().toISOString(),
                        percentage: vat
                    }
                });
                //Adjust the grandtotal and the package price
                if (!document.isInclusiveVat) {
                    condiPackage.sum = condiPackage.sum.plus(summary.vatTotal);
                    grandTotal = grandTotal.plus(summary.vatTotal);
                }
                //Remember the vat total
                if (key == 0) {
                    vat0Total = summary.appliedTotal;
                }
                else if (key == 1) {
                    vat1Total = summary.appliedTotal;
                }
                else if (key == 2) {
                    vat2Total = summary.appliedTotal;
                }
            });
            //Remember the document total
            documentTotal = grandTotal.round(2);
            if (document.isRounding && !document.conditions.find(condi => condi.conditionLevel == 5)) {
                const rounded = moneyRound(condiPackage.sum);
                //Set the rounding dif on level 4
                condiPackage.roundingDif = rounded.minus(condiPackage.sum.round(2));
                condiPackage.sum = rounded;
                documentTotal = moneyRound(documentTotal);
            }
            continue;
        }
        for (let i = 0; i < targetConditions.length; i++) {
            const condition = targetConditions[i];
            let targetPos = pricePositions;
            // if the condition targets a specific position type group (e.g. "sonstiges") filter for them, additionally filter out net positions if the condition is set to ignore them
            if (condition.fkSystemPositionType) {
                targetPos = targetPos.filter(pos => pos.fkSystemPositionType == condition.fkSystemPositionType && !(condition.isIgnoreNet && pos.isNetto));
            }
            else {
                targetPos = targetPos.filter(pos => !(condition.isIgnoreNet && pos.isNetto));
            }
            //If we do not have any postions that the condition should be applied, we skip the calc
            if (targetPos.length <= 0) {
                //Build the display version of the condition.
                let conditionData = {
                    priceReductionIsApplied: new Big(0),
                    reductionValue: new Big(0).neg(),
                    skipped: true,
                    documentCondition: condition
                };
                //Add the line for printing.
                condiPackage.values.push(conditionData);
                continue;
            }
            //A map which holds the reduction per position. (vat, amount per pos.)
            let fixedValuePerPos = new Map();
            //Helper variable for calculating uneven condition value splitting. Example 100/3 = 33.33 rest 0.01
            let fixedValueRoundingDifference = new Big(0);
            // If the reduction is fixed, we have to calculate the reduction per position.
            if (condition.fixedValue) {
                //Helper variable to split a reduction value evenly over all positions. sum up all position prices into fixedGrandTotal
                let fixedGrandTotal = targetPos.reduce((accum, object) => {
                    return accum.plus(object.reducedPrice.abs());
                }, new Big(0));
                fixedValueRoundingDifference = condition.fixedValue;
                targetPos.forEach((pos, index) => {
                    let fixedValue = new Big(0);
                    // edge case: if the positions total to 0, we have to split the reduction evenly over all positions, this in turn is technically only correct when all positions equal 0 as well.
                    // In all other cases, the reduction now happens for both positive and negative positions in the direction of -infinity, which is not correct. But we decided to ignore this
                    // ultra edge case, as otherwise we have the problem of dividing by 0.
                    if (fixedGrandTotal.eq(0)) {
                        fixedValue = condition.fixedValue.div(targetPos.length).round(2);
                    }
                    else {
                        fixedValue = pos.reducedPrice.abs().div(fixedGrandTotal).times(condition.fixedValue).round(2);
                    }
                    fixedValuePerPos.set(index, fixedValue);
                    fixedValueRoundingDifference = fixedValueRoundingDifference.minus(fixedValue);
                });
            }
            //What is the total reduction we did on all positions.
            // For the correct display of the full reduction we need to add the fixedValueRoundingDifference after splitting the fixed value.
            let displayConditionReduction = fixedValueRoundingDifference;
            //What is the price we applied the values on.
            let displayAppliedPrice = fixedValueRoundingDifference;
            targetPos.forEach((pos, index) => {
                //Get the reduction amount we have to apply
                let conditionReductionValue = new Big(0);
                //Choose the right price depending on the sequential flag.
                let priceUsedToCalcReduction = pos.reducedPrice;
                //If we have a non-sequential price, we have to take the last saved price and do our calculations on that.
                if (!condition.isSequential) {
                    //We have to take the element from the specific condition level. If this does not have an entry yet, we will take the last history entry. If none exists, take the target price
                    priceUsedToCalcReduction = pos.historyPrises[condition.conditionLevel - 1] || getLastValidHistoryPrice(pos, condition.conditionLevel - 1) || priceUsedToCalcReduction;
                }
                // to correct the fixed value rounding difference we have to reduce the first position by the difference
                if (index == 0) {
                    //We must correct it on he reduced price as well as on the current target price, which can be a history value.
                    pos.reducedPrice = pos.reducedPrice.minus(fixedValueRoundingDifference);
                    priceUsedToCalcReduction = priceUsedToCalcReduction.minus(fixedValueRoundingDifference);
                }
                if (condition.percentage) {
                    //Calculate the new price with the percentages.
                    conditionReductionValue = priceUsedToCalcReduction.div(100).times(condition.percentage).round(2);
                }
                else if (condition.fixedValue) {
                    conditionReductionValue = fixedValuePerPos.get(index);
                }
                //Add the target price to the applied price.
                displayAppliedPrice = displayAppliedPrice.plus(priceUsedToCalcReduction);
                //Set the new actual value.
                pos.reducedPrice = pos.reducedPrice.minus(conditionReductionValue);
                //Add the reduction we applied.
                displayConditionReduction = displayConditionReduction.plus(conditionReductionValue);
                //Save the new actual value on the condition level.
                pos.historyPrises[condition.conditionLevel] = pos.reducedPrice;
            });
            displayAppliedPrice = displayAppliedPrice.round(2);
            displayConditionReduction = displayConditionReduction.round(2);
            //Get the percentage
            let percentage = condition.percentage;
            let fixedValue = condition.fixedValue;
            //If we have additions we have to invert it.
            if (levelIndex == 3) {
                if (percentage) {
                    percentage = percentage.neg();
                }
                if (fixedValue) {
                    fixedValue = fixedValue.neg();
                }
            }
            //Build the display version of the condition.
            let conditionData = {
                priceReductionIsApplied: displayAppliedPrice,
                reductionValue: displayConditionReduction.neg(),
                skipped: false,
                documentCondition: condition
            };
            //Add the line for printing.
            condiPackage.values.push(conditionData);
            //Set the new sum
            condiPackage.sum = condiPackage.sum.minus(displayConditionReduction);
            grandTotal = grandTotal.minus(displayConditionReduction);
        }
        //If we do not have any condition on this level, set the history price, so tha we can read it later on.
        if (targetConditions.length <= 0) {
            pricePositions.forEach(pos => {
                //Save the new actual value on the condition level.
                pos.historyPrises[levelIndex] = pos.reducedPrice;
            });
        }
        // the last package total needs to be rounded to the nearest 0.05 if the document is set to round,
        // so the last total is equal to the grand total
        if (levelIndex == 5 && document.isRounding) {
            const rounded = moneyRound(condiPackage.sum);
            //Set the rounding dif only if we have values
            if (condiPackage.values.length > 0) {
                condiPackage.roundingDif = rounded.minus(condiPackage.sum.round(2));
            }
            condiPackage.sum = rounded;
        }
    }
    if (document.isRounding) {
        grandTotal = moneyRound(grandTotal);
    }
    else {
        grandTotal = grandTotal.round(2);
    }
    return {
        startTotal: startTotal,
        total: documentTotal,
        vat0Total: vat0Total,
        vat1Total: vat1Total,
        vat2Total: vat2Total,
        hasSBVPositions: hasSBVPositions,
        amountToPay: grandTotal,
        hoursWorked: hoursWorked,
        totalPerGroupWithoutNetto: groupTotals,
        totalPerRapport: totalPerRapport,
        conditionPackes: Array.from(conditionPackages.values())
    };
}
/**
 * Get the last valid history price for the target level. This will iterate backwards incase the target level does not have a valid
 * price. This means the index returns undefinde.
 * @param calculationPosition The postion we analyse
 * @param targetLevel The level we look for
 * @returns The last valid history price.
 */
export function getLastValidHistoryPrice(calculationPosition, targetLevel) {
    for (let index = targetLevel; index >= 0; index--) {
        const historyPrise = calculationPosition.historyPrises[index];
        if (historyPrise != undefined)
            return historyPrise;
    }
}
